import { useToast } from '@/hooks/use-toast';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';

interface FeedbackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateId: number;
  sessionId: string;
  onFeedbackSubmitted: () => void;
}

const FEEDBACK_MAX_LENGTH = 1000;
const INSTRUCTIONS_MAX_LENGTH = 2000;

interface TemplateInfo {
  created_by_id: number | null;
  custom_prompt: string | null;
  is_personal?: boolean;
}

async function fetchTemplateInfo(templateId: number): Promise<TemplateInfo> {
  const response = await fetch(`/api/templates/${templateId}`, {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error('Failed to fetch template info');
  }
  return response.json();
}

export const FeedbackModal = ({
  open,
  onOpenChange,
  templateId,
  sessionId,
  onFeedbackSubmitted,
}: FeedbackModalProps) => {
  const [feedbackText, setFeedbackText] = useState('');
  const [customInstructions, setCustomInstructions] = useState<string | null>(null);
  const { toast } = useToast();

  const { data: templateInfo, isLoading: isLoadingTemplate } = useQuery({
    queryKey: ['template-info', templateId],
    queryFn: () => fetchTemplateInfo(templateId),
    enabled: open && !!templateId,
  });

  const isPersonalTemplate = templateInfo?.is_personal === true;
  const initialInstructions = templateInfo?.custom_prompt || '';
  const currentInstructions = customInstructions ?? initialInstructions;

  const saveMutation = useMutation({
    mutationFn: async () => {
      const promises: Promise<Response>[] = [];

      if (feedbackText.trim()) {
        promises.push(
          fetch('/api/template-feedback', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              audio_id: sessionId,
              template_id: templateId,
              rating: 'negative',
              feedback_text: feedbackText.trim(),
            }),
          })
        );
      }

      if (isPersonalTemplate && currentInstructions.trim() !== initialInstructions.trim()) {
        promises.push(
          fetch(`/api/templates/${templateId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              custom_prompt: currentInstructions.trim(),
            }),
          })
        );
      }

      if (promises.length > 0) {
        const results = await Promise.all(promises);
        const allSuccessful = results.every((r) => r.ok);
        if (!allSuccessful) {
          throw new Error('One or more requests failed');
        }
      }
    },
    onSuccess: () => {
      onOpenChange(false);
      onFeedbackSubmitted();

      let message = 'Takk fyrir endurgjöfina!';
      if (feedbackText.trim() && currentInstructions.trim() !== initialInstructions.trim()) {
        message = 'Takk fyrir endurgjöfina og leiðbeiningarnar hafa verið vistaðar!';
      } else if (currentInstructions.trim() !== initialInstructions.trim()) {
        message = 'Leiðbeiningarnar hafa verið vistaðar!';
      }
      toast({ title: message });
    },
    onError: () => {
      toast({
        title: 'Villa',
        description: 'Ekki tókst að vista. Reyndu aftur.',
        variant: 'destructive',
      });
    },
  });

  const handleSave = () => {
    saveMutation.mutate();
  };

  // Show tabs only if it's a personal template
  const showTabs = isPersonalTemplate && !isLoadingTemplate;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Bæta sniðmátið</DialogTitle>
        </DialogHeader>

        {showTabs ? (
          <Tabs defaultValue="feedback" className="mt-2">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="feedback">Endurgjöf</TabsTrigger>
              <TabsTrigger value="instructions">Sérsniðnar leiðbeiningar</TabsTrigger>
            </TabsList>

            <TabsContent value="feedback" className="mt-4">
              <p className="mb-3 text-muted-foreground text-sm">Hvað mætti vera betra við þessa samantekt?</p>
              <Textarea
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value.slice(0, FEEDBACK_MAX_LENGTH))}
                maxLength={FEEDBACK_MAX_LENGTH}
                placeholder="t.d. Vantaði að taka fram lyf sem sjúklingur tekur núna..."
                rows={4}
                className="resize-none"
              />
              <div className="mt-2 flex justify-between text-muted-foreground text-xs">
                <span>Þessi endurgjöf hjálpar okkur að gera samantektir betri</span>
                <span>
                  {feedbackText.length}/{FEEDBACK_MAX_LENGTH}
                </span>
              </div>
            </TabsContent>

            <TabsContent value="instructions" className="mt-4">
              <p className="mb-3 text-muted-foreground text-sm">
                Þessar leiðbeiningar hjálpa gervigreindinni að búa til betri samantektir.
              </p>
              <Textarea
                value={currentInstructions}
                onChange={(e) => setCustomInstructions(e.target.value.slice(0, INSTRUCTIONS_MAX_LENGTH))}
                maxLength={INSTRUCTIONS_MAX_LENGTH}
                placeholder="t.d. Alltaf taka fram lyf sem sjúklingur tekur núna..."
                rows={6}
                className="resize-none"
              />
              <div className="mt-2 flex justify-between text-muted-foreground text-xs">
                <span>Notað fyrir allar næstu samantektir með þessu sniðmáti</span>
                <span>
                  {currentInstructions.length}/{INSTRUCTIONS_MAX_LENGTH}
                </span>
              </div>
            </TabsContent>
          </Tabs>
        ) : (
          <div className="mt-4">
            <p className="mb-3 text-muted-foreground text-sm">Hvað mætti vera betra við þessa samantekt?</p>
            <Textarea
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value.slice(0, FEEDBACK_MAX_LENGTH))}
              maxLength={FEEDBACK_MAX_LENGTH}
              placeholder="t.d. Vantaði að taka fram lyf sem sjúklingur tekur núna..."
              rows={4}
              className="resize-none"
            />
            <div className="mt-2 flex justify-between text-muted-foreground text-xs">
              <span>Þessi endurgjöf hjálpar okkur að gera samantektir betri</span>
              <span>
                {feedbackText.length}/{FEEDBACK_MAX_LENGTH}
              </span>
            </div>
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saveMutation.isPending}>
            Hætta við
          </Button>
          <Button onClick={handleSave} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Vista...' : 'Vista breytingar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
